if (!window.customElements.get('native-product-recommendations')) {
  class NativeProductRecommendations extends HTMLElement {

    connectedCallback() {
      this.pageSize = Number(this.getAttribute('data-page-size')) || 8;
      const dataScript = this.querySelector('[data-native-products]');
      if (!dataScript) return;
      let products = [];
      try {
        products = JSON.parse(dataScript.textContent);
      } catch (error) {
        console.error('native-product-recommendations: invalid product JSON', error);
        return;
      }
      if (!products.length) return;
      // The card builder arrives via deferred scripts and, with local data,
      // there is no API round trip absorbing the gap — wait for it.
      this.whenReady(() => this.render(products.slice(0, this.pageSize)));
    }

    whenReady(fn, attempts = 200) {
      if (window.renderProductCard && window.theme && window.theme.ProductCard
        && window.jQuery && window.jQuery.fn && window.jQuery.fn.slick) {
        fn();
        return;
      }
      if (attempts <= 0) return;
      setTimeout(() => this.whenReady(fn, attempts - 1), 50);
    }

    gridSizingClass() {
      const columns = Number(this.getAttribute('data-desktop-columns')) || 5;
      switch (columns) {
        case 2: return 'medium-up--one-half';
        case 3: return 'medium-up--one-third';
        case 4: return 'medium-up--one-quarter';
        case 6: return 'medium-up--one-sixth';
        default: return 'medium-up--one-fifth';
      }
    }

    render(products) {
      this.productWrap = this.querySelector('[data-product-wrap]') || this;
      // connectedCallback re-fires when the editor re-renders this element;
      // a copied slick-initialized class would make re-init a silent no-op
      // (bare grid), so tear stale state down first.
      if (this.slider || this.productWrap.classList.contains('slick-initialized')) {
        try { $(this.productWrap).slick('unslick'); } catch (error) {}
        this.productWrap.classList.remove('slick-initialized', 'slick-slider');
        this.slider = null;
      }

      const isSlider = this.hasAttribute('data-native-slider');
      const sizing = this.gridSizingClass();
      // Carousel cards carry no theme grid/swiper classes at all: the
      // per-instance CSS (pre-slick) and slick's inline widths own their
      // geometry, so foreign stylesheets (swiper-bundle on the homepage,
      // theme grid gutters) cannot distort them. Grid layout keeps the
      // theme grid classes and its native gutters.
      const gridSizing = isSlider
        ? ''
        : 'grid__item small--one-half ' + sizing;

      this.productWrap.innerHTML = products.map(product => {
        return window.renderProductCard(product, { gridSizing: gridSizing })
      }).join('')
      // xgen multi-variant cards keep a size selector visible under the
      // product info; rework the theme's tap-to-reveal overlay into that
      // model before ProductCard binds its handlers.
      if (isSlider) {
        this.productWrap.querySelectorAll('.product-card').forEach(card => this.setupInlineVariants(card));
      }
      this.productWrap.querySelectorAll('.product-card').forEach(productCard => {
        // One bad card must not abort the loop and leave the slider dead;
        // theme.ProductCard is the destroy-then-create initialiser, so
        // re-renders never double-bind listeners.
        try {
          window.theme.ProductCard(productCard);
        } catch (error) {
          console.error('native-product-recommendations: card init failed', error);
        }
      })

      // Below 4 cards slick mis-sizes its slides (count below slidesToShow);
      // the grid__item width classes already render a correct static row.
      if (isSlider && products.length >= 4) this.initSlick()
    }

    // xgen's selector model: pills under the info SELECT a variant (active
    // state, form select synced) and the always-present bag button submits
    // the selection through the theme's normal add flow. The theme's model
    // (bag toggles an overlay, pill click adds immediately) is disabled by
    // renaming the classes its handlers key on, before ProductCard init.
    setupInlineVariants(card) {
      const picker = card.querySelector('.product-card__variant-picker');
      if (!picker) return;
      card.appendChild(picker);
      card.classList.add('product-card--inline-variants');
      const select = card.querySelector('[data-product-select]');

      card.querySelectorAll('.product__add--show-variants').forEach(btn => {
        btn.classList.remove('product__add--show-variants');
        if (!btn.classList.contains('product__add--mobile')) btn.classList.add('product__add');
        btn.setAttribute('type', 'submit');
        btn.setAttribute('name', 'add');
        btn.setAttribute('data-add-to-cart', '');
      });

      const options = [];
      picker.querySelectorAll('.product-card__variants .add-product-variant').forEach(btn => {
        btn.classList.remove('add-product-variant');
        btn.classList.add('native-recs-variant-option');
        options.push(btn);
      });
      const applySelection = (id) => {
        options.forEach(o => o.classList.toggle('active', o.getAttribute('data-variant-id') === String(id)));
        if (select) select.value = String(id);
      };
      options.forEach(btn => {
        btn.addEventListener('click', (event) => {
          // The card is wrapped in a link: selecting a size must not
          // navigate or bubble into any card-level handler.
          event.preventDefault();
          event.stopPropagation();
          applySelection(btn.getAttribute('data-variant-id'));
        });
      });
      if (select && select.value) applySelection(select.value);
    }

    initSlick() {
      if (!this.productWrap.children.length) return;
      const gridMedium = window.theme && theme.settings && theme.settings.grid_medium ? Number(String(theme.settings.grid_medium).replace('px', '')) : 768;
      if (!this._modeQuery) {
        this._modeQuery = window.matchMedia('(min-width: ' + gridMedium + 'px)');
        this._modeQuery.addEventListener('change', () => this.applySliderMode());
      }
      this.applySliderMode();
    }

    applySliderMode() {
      const initialized = this.productWrap.classList.contains('slick-initialized');
      if (!this._modeQuery.matches) {
        // Below 768 the carousel is a native momentum scroll row (the
        // fallback flex layout doubles as it): slick's swipe has no
        // inertia — one card per flick — which reads stuttery next to
        // the xgen feel. Free flick + gentle CSS snapping instead.
        if (initialized) {
          try { $(this.productWrap).slick('unslick'); } catch (error) {}
          this.productWrap.classList.remove('slick-initialized', 'slick-slider');
          this.slider = null;
        }
        this.productWrap.setAttribute('data-slider-fallback', '');
        return;
      }
      if (initialized) return;

      // NEVER initialise slick while this element has no layout — inside the
      // pdp-recommendations accordion collapsed at load (`expand` off, the
      // theme-editor default) the content wrapper is display:none, slick
      // measures .slick-list as 0 and burns `width: 0px` into the track and
      // every slide, and the reveal only flips `display`, firing no resize.
      // That is the "vertical stack of full-width cards with a half-cropped
      // Add to Bag" the gift-card template surfaced. Hold the fallback
      // layout instead and re-enter here on first visibility.
      if (!this.productWrap.offsetWidth && 'IntersectionObserver' in window) {
        this.productWrap.setAttribute('data-slider-fallback', '');
        this.deferUntilVisible();
        return;
      }

      const desktopColumns = Number(this.getAttribute('data-desktop-columns')) || 5;
      const hasArrows = this.getAttribute('data-slider-arrows') === 'true';
      const hasDots = this.getAttribute('data-slider-dots') === 'true';

      const slickOptions = {
        slidesToShow: desktopColumns,
        // The xgen swiper used slidesPerGroup 1: arrows advance one product
        // and a drag settles on whichever product is nearest, not on page
        // boundaries. swipeToSlide gives slick the same settling behaviour.
        slidesToScroll: 1,
        swipeToSlide: true,
        // Default threshold (a fifth of the list) needs a full-card drag
        // before anything moves; 10 makes half a card the minimum swipe.
        touchThreshold: 10,
        dots: hasDots,
        arrows: hasArrows,
        infinite: false,
      };

      if (hasArrows) {
        slickOptions.prevArrow = this.querySelector('.slider__arrow-prev');
        slickOptions.nextArrow = this.querySelector('.slider__arrow-next');
      }
      if (hasDots) {
        const dots = this.querySelector('.slider__dots');
        if (dots) slickOptions.appendDots = dots;
      }

      // The pre-init fallback CSS keys on this attribute and slick only adds
      // .slick-initialized at the END of init — while it builds and measures,
      // the fallback's display:flex would wrap the freshly created
      // .slick-list, whose min-width:auto content width then poisons the
      // measurement (slides came out list-wide). Drop the net before slick
      // starts; restore it only if slick genuinely fails.
      this.productWrap.removeAttribute('data-slider-fallback');
      try {
        // Keep the arrows on the image's vertical middle across breakpoint
        // and resize changes; slick fires setPosition on all of them.
        $(this.productWrap).on('setPosition', () => this.positionArrows());
        this.slider = $(this.productWrap).slick(slickOptions);
        this.positionArrows();
      } catch (error) {
        this.productWrap.setAttribute('data-slider-fallback', '');
        console.error('native-product-recommendations: slick init failed', error);
      }
    }

    deferUntilVisible() {
      if (this._visibilityWatch) return;
      // An element inside a display:none ancestor has no box, so the
      // observer reports it as not intersecting; the accordion reveal gives
      // it one and the callback fires on the next frame — with a real width
      // to measure. One-shot: disconnected the moment init proceeds.
      this._visibilityWatch = new IntersectionObserver(entries => {
        const visible = entries.some(entry => entry.isIntersecting);
        if (!visible || !this.productWrap.offsetWidth) return;
        this._visibilityWatch.disconnect();
        this._visibilityWatch = null;
        this.applySliderMode();
      });
      this._visibilityWatch.observe(this);
    }

    positionArrows() {
      const viewport = this.querySelector('.native-recs__viewport');
      const image = this.productWrap.querySelector('.product-card__image');
      if (!viewport || !image) return;
      const viewportRect = viewport.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      // A card is taller than its image — name, price and Add to Bag sit below
      // it — so a plain top:50% centres on the card and reads as "too low".
      // Measure the image instead (ab-figma-usp.js positionRecArrows, L3332-3351).
      //
      // Bail out rather than write a bad value when the image has no layout yet.
      // The recently-viewed carousel fetches its products and renders
      // asynchronously, so the first setPosition can land before the images have
      // a box; writing 0 would pin the arrows to the top edge until the next
      // resize, whereas leaving the property unset keeps the CSS 50% fallback
      // until a real measurement arrives.
      //
      // The collapsed-accordion zero-width case this note used to disclaim is
      // now handled twice over: applySliderMode refuses to initialise slick
      // while the wrap has no width (deferUntilVisible re-enters on reveal),
      // and pdp-recommendations.liquid's expand handler fires setPosition on
      // any slick it reveals as the belt for instances initialised by older
      // bundles.
      if (imageRect.height <= 0) return;
      // The RAW image centre, relative to the viewport's top. Half the button
      // height is NOT subtracted here — the stylesheet's translateY(-50%) does
      // that, and doing both would lift the arrows 12px. See the note beside
      // .slider__arrow in snippets/native-product-recommendations.liquid.
      this.style.setProperty(
        '--native-recs-arrow-top',
        (imageRect.top - viewportRect.top + imageRect.height / 2) + 'px'
      );
    }
  }

  customElements.define('native-product-recommendations', NativeProductRecommendations);
}
